import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

// Cloudflare Turnstile widget (architecture.md §16). Renders the challenge and hands the
// token back via onToken. When no siteKey is provided (local dev), it emits a placeholder
// token — the API skips verification when TURNSTILE_SECRET is unset, keeping dev unblocked.
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

export function Turnstile({ siteKey, onToken }) {
  const containerRef = useRef(null);
  const cbRef = useRef(onToken);
  cbRef.current = onToken;

  useEffect(() => {
    if (!siteKey) {
      cbRef.current?.('dev-token');
      return undefined;
    }
    let cancelled = false;
    const render = () => {
      if (cancelled || !window.turnstile || !containerRef.current) return;
      containerRef.current.innerHTML = '';
      window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token) => cbRef.current?.(token),
        'error-callback': () => cbRef.current?.(''),
        'expired-callback': () => cbRef.current?.(''),
      });
    };

    if (window.turnstile) {
      render();
      return () => { cancelled = true; };
    }
    let script = document.querySelector(`script[src="${SCRIPT_URL}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = SCRIPT_URL;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', render);
    return () => {
      cancelled = true;
      script.removeEventListener('load', render);
    };
  }, [siteKey]);

  if (!siteKey) return null;
  return <div ref={containerRef} />;
}

Turnstile.propTypes = {
  siteKey: PropTypes.string,
  onToken: PropTypes.func.isRequired,
};
